// src/pages/CaseDetail/CaseDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { casesAPI } from '../../api/cases.js';
import { Breadcrumbs } from '../../components/common/Breadcrumbs/Breadcrumbs';
import { Loader } from '../../components/ui/Loader/Loader.jsx';
import { Button } from '../../components/ui/Button/Button.jsx';
import { formatDate } from '../../utils/formatters.js';

export const CaseDetail = () => {
  const { id } = useParams();
  const [caseItem, setCaseItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadCase();
  }, [id]);

  const loadCase = async () => {
    setIsLoading(true);
    try {
      const response = await casesAPI.getById(id);
      setCaseItem(response.data.data);
    } catch (error) {
      console.error('Error loading case:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <Loader text="Загрузка кейса..." />;
  }

  if (!caseItem) {
    return (
      <div className="container" style={{ paddingTop: '60px', textAlign: 'center' }}>
        <h2>Кейс не найден</h2>
        <Link to="/cases">
          <Button style={{ marginTop: '16px' }}>Вернуться к кейсам</Button>
        </Link>
      </div>
    );
  }

  const breadcrumbs = [
    { label: 'Кейсы', path: '/cases' },
    { label: caseItem.title, path: `/cases/${caseItem.id}` },
  ];

  return (
    <div style={{ backgroundColor: 'rgb(var(--c-bg))', minHeight: '100vh', paddingBottom: '40px' }}>
      <div className="container" style={{ paddingTop: '24px' }}>
        <Breadcrumbs items={breadcrumbs} />

        <div style={{
          marginTop: '32px',
          backgroundColor: 'rgb(var(--c-surface))',
          borderRadius: '20px',
          border: '1px solid rgb(var(--c-border))',
          overflow: 'hidden',
        }}>
          {/* Обложка */}
          <div style={{
            backgroundColor: 'rgb(var(--c-surface-2))',
            height: '400px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgb(var(--c-text-secondary))',
            fontSize: '16px',
          }}>
            {caseItem.cover_image ? 'Изображение' : 'Нет фото'}
          </div>

          {/* Контент */}
          <div style={{ padding: '40px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '16px',
              marginBottom: '16px',
            }}>
              <span style={{
                fontSize: '14px',
                fontWeight: 500,
                color: 'rgb(var(--c-accent))',
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
              }}>
                {caseItem.category}
              </span>
              <span style={{
                fontSize: '14px',
                color: 'rgb(var(--c-text-secondary))',
              }}>
                Клиент: {caseItem.client}
              </span>
            </div>

            <h1 style={{
              fontSize: '36px',
              fontWeight: 700,
              color: 'rgb(var(--c-text))',
              marginBottom: '16px',
            }}>
              {caseItem.title}
            </h1>

            {caseItem.description && (
              <p style={{
                fontSize: '18px',
                fontWeight: 300,
                color: 'rgb(var(--c-text-secondary))',
                marginBottom: '24px',
                lineHeight: 1.7,
              }}>
                {caseItem.description}
              </p>
            )}

            {caseItem.full_description && (
              <div style={{
                fontSize: '16px',
                fontWeight: 300,
                color: 'rgb(var(--c-text-secondary))',
                lineHeight: 1.8,
                marginBottom: '24px',
                whiteSpace: 'pre-wrap',
              }}>
                {caseItem.full_description}
              </div>
            )}

            {/* Результаты */}
            {caseItem.results && Object.keys(caseItem.results).length > 0 && (
              <div style={{
                backgroundColor: 'rgb(var(--c-bg))',
                borderRadius: '16px',
                padding: '24px',
                marginBottom: '24px',
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: 'rgb(var(--c-text))',
                  marginBottom: '12px',
                }}>
                  Результаты
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
                  {Object.entries(caseItem.results).map(([key, value]) => (
                    <div key={key}>
                      <div style={{
                        fontSize: '12px',
                        color: 'rgb(var(--c-text-secondary))',
                        textTransform: 'uppercase',
                        letterSpacing: '0.02em',
                      }}>
                        {key}
                      </div>
                      <div style={{
                        fontSize: '20px',
                        fontWeight: 700,
                        color: 'rgb(var(--c-accent))',
                      }}>
                        {value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Теги */}
            {caseItem.tags && caseItem.tags.length > 0 && (
              <div style={{
                display: 'flex',
                gap: '8px',
                flexWrap: 'wrap',
                marginBottom: '24px',
              }}>
                {caseItem.tags.map((tag) => (
                  <span
                    key={tag}
                    style={{
                      padding: '4px 12px',
                      backgroundColor: 'rgb(var(--c-surface-2))',
                      borderRadius: '100px',
                      fontSize: '12px',
                      color: 'rgb(var(--c-text-secondary))',
                    }}
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: '24px',
              borderTop: '1px solid rgb(var(--c-border))',
              flexWrap: 'wrap',
              gap: '12px',
            }}>
              <span style={{ fontSize: '14px', color: 'rgb(var(--c-text-secondary))' }}>
                Завершен: {caseItem.completed_at ? formatDate(caseItem.completed_at) : 'Не указано'}
              </span>
              <div style={{ display: 'flex', gap: '12px' }}>
                <Link to="/cases">
                  <Button variant="secondary">Все кейсы</Button>
                </Link>
                <Link to="/contact">
                  <Button>Обсудить проект</Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};